import { Components } from "appworks/components/components";
import { HeaderComponent } from "appworks/components/header/header-component";
import { PromptComponent } from "appworks/components/prompt/prompt-component";
import { canvasManager } from "appworks/graphics/canvas/canvas-manager";
import { Orientation } from "appworks/graphics/canvas/orientation";
import { Services } from "appworks/services/services";
import { arrayToPoints } from "appworks/utils/collection-utils";
import { deviceInfo } from "appworks/utils/device-info";
import { TGRFakeSymbolValueComponent } from "components/tgr-fake-symbol-value";
import { TGRFreespinIntroComponent } from "components/tgr-freespin-intro";
import { TGRInstantWinsComponent } from "components/tgr-instant-wins";
import { TGRJackpotValuesComponent } from "components/tgr-jackpot-values";
import { TGRPreloaderComponent } from "components/tgr-preloader";
import { TGRReelDustComponent } from "components/tgr-reel-dust";
import { lines } from "config/lines";
import { reelsets } from "config/reelsets";
import { symbols } from "config/symbols";
import { EpicUIColourComponent } from "epic/components/epic-ui-colour-component";
import { EpicSetup } from "epic/epic-setup";
import { GamevyFooterComponent } from "gamevy/components/gamevy-footer-component";
import { gamevy } from "gamevy/gamevy";
import { gamevyJurisdiction, gamevyModel } from "gamevy/gamevy-model";
import { PartnerConnectAlertComponent } from "partner-connect/components/partner-connect-alert-component";
import { PartnerConnectManager } from "partner-connect/partner-connect-manager";
import { PartnerConnectCurrency } from "partner-connect/services/partner-connect-currency";
import { AlertComponent } from "slotworks/components/alert/alert-component";
import { BetBarComponent } from "slotworks/components/betbar/bet-bar-component";
import { CelebrateWinComponent } from "slotworks/components/celebrate-win/celebrate-win-component";
import { BannerFactory } from "slotworks/components/factories/banner-factory";
import { GenericComponentFactory } from "slotworks/components/factories/generic-component-factory";
import { PaytableFactory } from "slotworks/components/factories/paytable-factory";
import { FooterComponent } from "slotworks/components/footer/footer-component";
import { GambleComponent } from "slotworks/components/gamble/gamble-component";
import { slotDefinition } from "slotworks/model/slot-definition";
import { TGRBigWinFactory } from "./factories/tgr-big-win-factory";
import { TGRLinesComponentFactory } from "./factories/tgr-lines-component-factory";
import { TGRMatrixFactory } from "./factories/tgr-matrix-factory";
import { TGRSetupDebug } from "./tgr-setup-debug";
import { TGRSetupI18n } from "./tgr-setup-i18n";
import { TGRSetupIntegration } from "./tgr-setup-integration";
import { TGRSetupLayers } from "./tgr-setup-layers";
import { TGRSetupPartnerConnect } from "./tgr-setup-partner-connect";
import { TGRSetupSound } from "./tgr-setup-sound";
import { TGRSetupStates } from "./tgr-setup-states";
import { TGRSetupTransitions } from "./tgr-setup-transitions";
import { TGRSetupUI } from "./tgr-setup-ui";

declare const __ENVIRONMENT__: string;
export class TGRSetup extends EpicSetup {

    public setupLayers(): void {
        super.setupLayers();
        TGRSetupLayers();
    }

    public setupDebug(): void {
        super.setupDebug();
        TGRSetupDebug();
    }

    public setupCanvas(): void {
        deviceInfo.selectLayout({
            desktop: "desktop",
            tablet: "desktop",
            phone: "desktop"
        });

        if (!deviceInfo.isDesktop) {
            canvasManager.validOrientations = [Orientation.LANDSCAPE];
        }
        canvasManager.fullScreenEnabled = true;
        canvasManager.allowPreloaderRotationPrompt = true;
    }

    public setupServices(): void {
        super.setupServices();

        Services.CurrencyType = PartnerConnectCurrency;

        gamevy.PREPARE_RULES_URL = false;

        gamevyModel.jurisdictionId = PartnerConnectManager.getConfig().customParameters.license;
        gamevyModel.jurisdiction = gamevyJurisdiction.get(gamevyModel.jurisdictionId);
    }

    public setupComponents(): void {
        Components.register(new GenericComponentFactory(PartnerConnectAlertComponent), null);
        Components.register(new GenericComponentFactory(GamevyFooterComponent), 0);
        Components.register(new GenericComponentFactory(AlertComponent), 0);

        Components.register(new GenericComponentFactory(TGRPreloaderComponent), 0);

        Components.register(new BannerFactory());
        Components.register(new GenericComponentFactory(PromptComponent));
        Components.register(new GenericComponentFactory(HeaderComponent));
        Components.register(new GenericComponentFactory(FooterComponent));
        Components.register(new GenericComponentFactory(BetBarComponent));
        Components.register(new GenericComponentFactory(GambleComponent));
        Components.register(new GenericComponentFactory(CelebrateWinComponent));
        Components.register(new TGRLinesComponentFactory(slotDefinition.lines.values));
        Components.register(new PaytableFactory());

        Components.register(new TGRMatrixFactory());
        Components.register(new TGRBigWinFactory());
        Components.register(new GenericComponentFactory(EpicUIColourComponent));
        Components.register(new GenericComponentFactory(TGRInstantWinsComponent));
        Components.register(new GenericComponentFactory(TGRFakeSymbolValueComponent));
        Components.register(new GenericComponentFactory(TGRJackpotValuesComponent));
        Components.register(new GenericComponentFactory(TGRReelDustComponent));
        Components.register(new GenericComponentFactory(TGRFreespinIntroComponent));
    }

    public setupComms(): void {
        super.setupComms();
        TGRSetupIntegration();
    }

    public setupUI(): void {
        TGRSetupUI();
    }

    public setupStates(): void {
        super.setupStates();
        TGRSetupStates();
    }

    public setupTransitions(): void {
        super.setupTransitions();
        TGRSetupTransitions();
    }

    public setupSound(): void {
        super.setupSound();
        TGRSetupSound();
    }

    public setupI18N(): void {
        super.setupI18N();
        TGRSetupI18n();
    }

    public setupGameDefinition(): void {
        super.setupGameDefinition();

        slotDefinition.matrixGrid = [4, 4, 4, 4, 4];
        slotDefinition.lines.values = arrayToPoints(lines);
        slotDefinition.processSymbols(symbols);
        slotDefinition.processReelSets(reelsets);
    }

    public onReady(): void {
        super.onReady();
        /// TODO: YGG SPECIFIC STUFF THAT NEEDS TO BE MOVED OUT OF THE GAME
        if ((window as any).epicEnableCheats && __ENVIRONMENT__ === "production") {
            this.setupDebug();
        }
        /// ---
        PartnerConnectManager.ready();
        TGRSetupPartnerConnect();
    }
}
